import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SideNavButtons from "../components/Common/SideNavButtons";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";
import CategoryHeading from "../components/Common/CategoryHeading.js";
import Slider from "@mui/material/Slider";
import mqtt from "mqtt";
import { useCart } from "../context/CartContext";
import LoadingState from "../components/ImageGallery/loading_state_component";
import ErrorState from "../components/ImageGallery/error_state_component";
import EmptyState from "../components/ImageGallery/empty_state_component";
import CameraFilter from "../components/ImageGallery/camera_filter_component";
import ImageCard from "../components/ImageGallery/image_card_component";
import ImageModal from "../components/ImageGallery/image_modal_component";
import "../styles/ImageGallery.css";

const ImageGallery = () => {
  const WORKER_URL = "https://r2-image-service.t9e2i3kal.workers.dev";
  const UPLOAD_WORKER_URL =
    "https://image-service-uploader.t9e2i3kal.workers.dev";

  // Memoize MQTT_CONFIG to prevent unnecessary re-renders
  const MQTT_CONFIG = useMemo(
    () => ({
      brokerUrl:
        "wss://6225f5860bef41b88da4d2b83687e9b9.s1.eu.hivemq.cloud:8884/mqtt",
      username: "datkiosk-client",
      password: "QZ7P7KPCZ4Q@je4",
      topic: "image_uploaded",
      clientId: `gallery-${Math.random().toString(36).substr(2, 9)}`,
    }),
    []); // Empty dependency array since all values are static

  const MQTT_IMAGE_DELAY_MS = 1000;

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [metadataProcessed, setMetadataProcessed] = useState(false);
  const [hasUploadAuth, setHasUploadAuth] = useState(false);
  const [deletingImages, setDeletingImages] = useState({});
  const [cameraFilter, setCameraFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    // Load from localStorage if available, otherwise use default
    const savedItemsPerPage = localStorage.getItem('imageGalleryItemsPerPage');
    return savedItemsPerPage ? parseInt(savedItemsPerPage) : 24;
  });
  const [columns, setColumns] = useState(() => {
    // Load from localStorage if available, otherwise use responsive default
    const savedColumns = localStorage.getItem('imageGalleryColumns');
    return savedColumns ? parseInt(savedColumns) : (window.innerWidth > 768 ? 3 : 1);
  });
  
  const navigate = useNavigate();

  const handlePrev = () => {
    navigate('/packages');
  };

  const handleNext = () => {
    navigate('/items-catalog');
  };
  
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setColumns(1); // Force 1 column on mobile
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Save to localStorage whenever columns changes
    localStorage.setItem('imageGalleryColumns', columns.toString());
  }, [columns]);

  useEffect(() => {
    // Save to localStorage whenever itemsPerPage changes
    localStorage.setItem('imageGalleryItemsPerPage', itemsPerPage.toString());
  }, [itemsPerPage]);
  
  const [, setMqttConnected] = useState(false);
  const [, setMqttError] = useState(null);
  const { addToCart, removeFromCart, items, photoPrice } = useCart();
  const mqttClient = useRef(null);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    const sessionAuth = sessionStorage.getItem("uploadAuth");
    const localAuth = localStorage.getItem("uploadAuth");
    console.log(
      "Checking storage for uploadAuth - session:",
      sessionAuth,
      "local:",
      localAuth
    );
    setHasUploadAuth(!!(sessionAuth || localAuth));
  }, []);

  const fetchMetadata = useCallback(
    async (filename) => {
      try {
        const resp = await fetch(
          `${UPLOAD_WORKER_URL}/api/metadata/${encodeURIComponent(filename)}`
        );
        if (!resp.ok) return null;

        let data = await resp.json();

        if (data && typeof data === "object" && data.metadata)
          data = data.metadata;

        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch {
            data = null;
          }
        }
        return data && typeof data === "object" ? data : null;
      } catch {
        return null;
      }
    },
    [UPLOAD_WORKER_URL]
  );

  const attachMetadata = useCallback(
    async (img, onComplete) => {
      const meta = await fetchMetadata(img.name);
      onComplete?.();
      if (!meta) return;
      setImages((prev) =>
        prev.map((i) => (i.key === img.key ? { ...i, metadata: meta } : i))
      );
    },
    [fetchMetadata]
  );

  const listR2Objects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${WORKER_URL}/api/media`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to fetch media");
      setImages(json.data);
      let attempts = 0;
      json.data.forEach((i) =>
        attachMetadata(i, () => {
          attempts++;
          if (attempts >= json.data.length) {
            setMetadataProcessed(true);
          }
        })
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [WORKER_URL, attachMetadata]);

  useEffect(() => {
    const getType = (fn) =>
      ["mp4", "webm", "mov", "avi", "mkv", "m4v", "3gp", "flv"].includes(
        fn.toLowerCase().split(".").pop()
      )
        ? "video"
        : "image";

    const addNew = (d) => {
      const img = {
        key: d.filename,
        name: d.filename,
        url: d.url,
        size: d.size,
        lastModified: d.timestamp,
        type: getType(d.filename),
      };
      setImages((p) => (p.some((i) => i.key === img.key) ? p : [img, ...p]));
      attachMetadata(img);
    };

    try {
      mqttClient.current = mqtt.connect(MQTT_CONFIG.brokerUrl, {
        clientId: MQTT_CONFIG.clientId,
        username: MQTT_CONFIG.username,
        password: MQTT_CONFIG.password,
        clean: true,
        connectTimeout: 10_000,
        reconnectPeriod: 5_000,
      });

      mqttClient.current.on("connect", () => {
        setMqttConnected(true);
        setMqttError(null);
        mqttClient.current.subscribe(MQTT_CONFIG.topic);
      });

      mqttClient.current.on("message", (_, msg) => {
        try {
          const d = JSON.parse(msg.toString());
          if (d.url && d.filename) {
            setTimeout(() => addNew(d), MQTT_IMAGE_DELAY_MS);
          } else if (d.type === "bulk_delete" || d.type === "file_delete") {
            listR2Objects();
          }
        } catch (e) {
          console.error("Bad MQTT message", e);
        }
      });

      mqttClient.current.on("error", () => {
        setMqttError("Connection to live updates failed");
        setMqttConnected(false);
      });
      mqttClient.current.on("offline", () => setMqttConnected(false));
    } catch {
      setMqttError("Failed to connect to live updates");
    }
    return () => mqttClient.current?.end();
  }, [attachMetadata, listR2Objects, MQTT_CONFIG, MQTT_IMAGE_DELAY_MS]);

  useEffect(() => {
    listR2Objects();
    const id = setInterval(listR2Objects, 300_000);
    return () => clearInterval(id);
  }, [listR2Objects]);

  const cameraOptions = useMemo(() => {
    const cameraCounts = {};
    images.forEach((i) => {
      if (i.metadata?.camera) {
        cameraCounts[i.metadata.camera] =
          (cameraCounts[i.metadata.camera] || 0) + 1;
      }
    });
    return Object.entries(cameraCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([camera, count]) => ({ camera, count }));
  }, [images]);

  const allCount = images.length;
  const inCart = (k) => items.some((it) => it.key === k);
  const imgsFiltered = useMemo(
    () =>
      cameraFilter === "all"
        ? images
        : images.filter((i) => i.metadata?.camera === cameraFilter),
    [images, cameraFilter]
  );

  // Pagination logic
  const totalPages = Math.ceil(imgsFiltered.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = imgsFiltered.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to first page when filter or items per page changes
  useEffect(() => {
    setCurrentPage(1);
    // Scroll to top when filters change
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, [cameraFilter, itemsPerPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of gallery when changing pages
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  if (loading) return <LoadingState />;
  if (error && images.length === 0)
    return <ErrorState error={error} onRetry={listR2Objects} />;

  return (
    <div className="gallery-container">
      <CategoryHeading
        title="PHOTOS & VIDEOS"
        description="All photographs are permanently deleted within 24h."
      />

      <SideNavButtons 
        onPrev={handlePrev}
        onNext={handleNext}
        prevPageName="PACKAGES"
        nextPageName="MACHINE GUNS"
      />

      <button
        className="screensaver-btn"
        onClick={() => (window.location.href = "/slideshow")}
        aria-label="Start Slideshow"
      >
        <Play size={20} />
      </button>

      <div className="filter-controls">
        <CameraFilter
          metadataProcessed={metadataProcessed}
          images={images}
          cameraFilter={cameraFilter}
          setCameraFilter={setCameraFilter}
          cameraOptions={cameraOptions}
          allCount={allCount}
        />
      </div>
      
      <div className="slider-controls">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#ccc', fontSize: '14px' }}>Columns:</span>
            <Slider
              value={columns}
              onChange={(e, newValue) => setColumns(newValue)}
              min={1}
              max={6}
              step={1}
              valueLabelDisplay="auto"
              aria-labelledby="columns-slider"
              sx={{
                width: 150,
                color: '#e6c618'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#ccc', fontSize: '14px' }}>Items:</span>
            <Slider
              value={itemsPerPage}
              onChange={(e, newValue) => setItemsPerPage(newValue)}
              min={6}
              max={48}
              step={6}
              valueLabelDisplay="auto"
              aria-labelledby="items-per-page-slider"
              sx={{
                width: 150,
                color: '#e6c618'
              }}
            />
          </div>
        </div>
      </div>

      {imgsFiltered.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className="pagination-info" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '0 1rem', 
            maxWidth: '1280px', 
            margin: '0 auto',
            color: '#ccc',
            fontSize: '14px'
          }}>
            <div>
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, imgsFiltered.length)} of {imgsFiltered.length} items
            </div>
            <div>
              Page {currentPage} of {totalPages}
            </div>
          </div>
          
          <div className="image-grid" style={{ '--columns': columns }}>
            {currentItems.map((m) => (
              <ImageCard
                key={m.key}
                media={m}
                isIOS={isIOS}
                setSelectedImage={setSelectedImage}
                hasUploadAuth={hasUploadAuth}
                deletingImages={deletingImages}
                setDeletingImages={setDeletingImages}
                setImages={setImages}
                inCart={inCart}
                removeFromCart={removeFromCart}
                addToCart={addToCart}
                photoPrice={photoPrice}
                UPLOAD_WORKER_URL={UPLOAD_WORKER_URL}
              />
            ))}
          </div>
          
          {totalPages > 1 && (
            <div className="pagination-controls" style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '20px', 
              padding: '2rem 1rem',
              maxWidth: '1280px', 
              margin: '0 auto'
            }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  background: currentPage === 1 ? '#333' : '#e6c618',
                  color: currentPage === 1 ? '#666' : '#010101',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500'
                }}
              >
                <ChevronLeft size={20} />
                Previous
              </button>
              
              <div style={{ 
                display: 'flex', 
                gap: '8px',
                alignItems: 'center'
              }}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        background: currentPage === pageNum ? '#e6c618' : '#252525',
                        color: currentPage === pageNum ? '#010101' : '#ccc',
                        border: '1px solid #333',
                        borderRadius: '4px',
                        width: '40px',
                        height: '40px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && (
                  <span style={{ color: '#ccc' }}>
                    ...
                  </span>
                )}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  background: currentPage === totalPages ? '#333' : '#e6c618',
                  color: currentPage === totalPages ? '#666' : '#010101',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500'
                }}
              >
                Next
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}

      <ImageModal
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
        imgsFiltered={imgsFiltered}
        inCart={inCart}
        removeFromCart={removeFromCart}
        addToCart={addToCart}
        photoPrice={photoPrice}
      />
    </div>
  );
};

export default ImageGallery;
